/**
 * br-video-defer — attach deferred <source data-src> videos on approach.
 *
 * Autoplay background videos with real <source src> download immediately,
 * even when display:none (the Science hero fetched ~12MB while hidden on
 * mobile) or far below the fold. Sections opting in render their sources
 * as data-src with preload="none" and no autoplay attribute; this utility
 * promotes data-src -> src and calls the section's onAttached callback
 * (its syncPlayback, which owns the play decision) only when:
 *
 *   - the user has NOT requested reduced motion (poster holds instead), and
 *   - the optional minWidth media condition matches (kills the download on
 *     layouts where the video is hidden), and
 *   - the video is within ~400px of the viewport.
 *
 * No-JS keeps the poster, matching the reduced-motion presentation.
 * Double-execution guarded like the br-motion utilities: sections each
 * emit this script tag before their own deferred script.
 */
(() => {
  if (window.brVideoDefer) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const attach = (video, { minWidth, onAttached } = {}) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.dataset.brVideoDeferReady === 'true') return;
    video.dataset.brVideoDeferReady = 'true';

    const deferredSources = [...video.querySelectorAll('source[data-src]')];
    if (deferredSources.length === 0) return;

    const widthGate = minWidth ? window.matchMedia(`(min-width: ${minWidth}px)`) : null;
    let attached = false;

    const promote = () => {
      if (attached || reducedMotion.matches) return;
      if (widthGate && !widthGate.matches) return;
      attached = true;

      deferredSources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();

      observer?.disconnect();
      widthGate?.removeEventListener('change', promote);
      reducedMotion.removeEventListener('change', promote);

      if (typeof onAttached === 'function') onAttached();
    };

    // Reduced motion / width are re-checked at attach time, so a user
    // relaxing either setting before the video approaches still gets it.
    reducedMotion.addEventListener('change', promote);
    widthGate?.addEventListener('change', promote);

    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) promote();
            },
            { rootMargin: '400px 0px' }
          )
        : null;

    if (observer) {
      observer.observe(video);
    } else {
      promote();
    }
  };

  window.brVideoDefer = { attach };
})();
